import type { ReactNode } from 'react';
import { createContext, use, useEffect, useRef } from 'react';
import { getStorageValue, useLocalStorage } from '@/hooks/browser/use-local-storage';
import { authStore } from '@/services/auth/store';
import { isBuilderPlan } from '@/services/auth/plan-slugs';
import { useStore } from '@nanostores/react';
import type { ColorSchemePreference } from '@customerio/pluma-components/react';
import { reconcileTheme, type ThemeReconcileResult } from '@/utils/theme-reconcile';

// Persist the theme to the backend via a lazy import so this widely-rendered context
// (the test providers wrap every browser test in it) doesn't pull `@/services/api`'s
// `hydraUiApi` into its static module graph. A static import propagated that dependency
// into every consumer, breaking browser tests that only partially mock `@/services/api`.
function persistThemePreference(accountId: number, userId: number, colorScheme: ColorSchemePreference) {
  void import('@/services/db/user-settings/mutations')
    .then(({ updateUserSettings }) =>
      updateUserSettings({ accountId, userId, themePreferences: { color_scheme: colorScheme } }),
    )
    .catch(() => {
      // Best-effort; localStorage already holds the value for this device.
    });
}

export const GlobalCSSVariable = {
  MainNavWidth: '--hydra-main-nav-width',
  MainNavTransitionTime: '--hydra-main-nav-transition-time', // set in index.scss, not usually set dynamically
  MainNavTransitionCurve: '--hydra-main-nav-transition-curve', // set in index.scss, not usually set dynamically
  TopNavHeight: '--hydra-top-nav-height',
  MasqBannerHeight: '--hydra-masq-banner-height',
  AssistantFooterHeight: '--hydra-assistant-footer-height',
  WorkspaceBorderHeight: '--hydra-workspace-border-height',
  WorkspaceBorderColor: '--hydra-workspace-border-color',
  WorkspaceDeliveryModeBannerHeight: '--hydra-workspace-delivery-mode-banner-height',
} as const;

type GlobalCSSVariable = (typeof GlobalCSSVariable)[keyof typeof GlobalCSSVariable];

interface IThemeContext {
  colorScheme: ColorSchemePreference;
  setColorScheme: (colorScheme: ColorSchemePreference) => void;
  setVariable: (variable: GlobalCSSVariable, value: string) => void;
  setVariables: (variables: Partial<Record<GlobalCSSVariable, string>>) => void;
  getVariable: (variable: GlobalCSSVariable) => string;
  removeVariable: (variable: GlobalCSSVariable) => void;
}

const ThemeContext = createContext<IThemeContext | null>(null);

export function useThemeContext() {
  const context = use(ThemeContext);
  if (context == null) {
    throw new Error('useThemeContext must be used within a ThemeContext');
  }
  return context;
}

const COLOR_SCHEME_KEY = 'color-scheme';
const COLOR_SCHEME_EXPLICIT_KEY = 'color-scheme-explicit';
const DEFAULT_COLOR_SCHEME: ColorSchemePreference = 'auto';

// One-time migration from the previous boolean-based keys
// (`dark-mode-enabled` / `dark-mode-explicit`) to the new
// `color-scheme` / `color-scheme-explicit` keys. Runs at module load
// before `useLocalStorage` reads, so existing user preferences carry
// across the rename without treating the previous implicit default as
// an explicit light preference.
(function migrateLegacyDarkModeKeys() {
  try {
    if (localStorage.getItem(COLOR_SCHEME_KEY) != null) return;

    const legacyValue = localStorage.getItem('dark-mode-enabled');
    if (legacyValue == null) return;

    const wasDark = JSON.parse(legacyValue) === true;
    const hadExplicitPreference = localStorage.getItem('dark-mode-explicit') != null;

    if (wasDark || hadExplicitPreference) {
      localStorage.setItem(COLOR_SCHEME_KEY, JSON.stringify(wasDark ? 'dark' : 'light'));
    }

    if (hadExplicitPreference) {
      localStorage.setItem(COLOR_SCHEME_EXPLICIT_KEY, 'true');
    }

    localStorage.removeItem('dark-mode-enabled');
    localStorage.removeItem('dark-mode-explicit');
  } catch {
    // localStorage may be unavailable (private mode, blocked) — skip migration
  }
})();

/**
 * A stored `'light'` with no recorded explicit choice is a leftover from when `'light'` was the
 * implicit default, not a preference the user ever expressed. Rewrite it to the current default
 * at module load — before `useLocalStorage` reads it — so it can never drive a paint.
 *
 * This has to happen in storage rather than being masked in render: the exposed scheme must stay
 * a plain reflection of React state. Deriving it from `COLOR_SCHEME_EXPLICIT_KEY` instead would
 * couple it to a non-reactive localStorage read, and the reconcile path writes that key without
 * necessarily changing state — leaving the context stranded on a stale value.
 *
 * Exported for tests; the module-load call below is what production relies on.
 */
export function normalizeImplicitLightPreference() {
  try {
    if (localStorage.getItem(COLOR_SCHEME_EXPLICIT_KEY) != null) return;
    if (getStorageValue<ColorSchemePreference | null>(COLOR_SCHEME_KEY, null) !== 'light') return;

    localStorage.setItem(COLOR_SCHEME_KEY, JSON.stringify(DEFAULT_COLOR_SCHEME));
  } catch {
    // localStorage may be unavailable (private mode, blocked) — skip
  }
}

normalizeImplicitLightPreference();

type ThemeContextProviderProps = { children: ReactNode };

export function getHasExplicitColorSchemePreference() {
  return getStorageValue<boolean>(COLOR_SCHEME_EXPLICIT_KEY, false);
}

// Apply a reconcile decision: cache the explicit flag, adopt the resolved scheme, and
// backfill the local choice to the backend when the backend has none.
function applyThemeReconcile(
  result: ThemeReconcileResult,
  apply: {
    setColorScheme: (scheme: ColorSchemePreference) => void;
    accountId: number;
    userId: number;
    colorScheme: ColorSchemePreference;
  },
) {
  if (result.setExplicit) {
    localStorage.setItem(COLOR_SCHEME_EXPLICIT_KEY, 'true');
  }
  if (result.next != null) {
    apply.setColorScheme(result.next);
  }
  if (result.backfill) {
    persistThemePreference(apply.accountId, apply.userId, apply.colorScheme);
  }
}

// Module-scoped so their identities are stable: consumers key effects on
// these, and the provider's context value object is rebuilt every render.
const setVariable = (variable: GlobalCSSVariable, value: string) => {
  document.documentElement.style.setProperty(variable, value);
};

const setVariables = (variables: Partial<Record<GlobalCSSVariable, string>>) => {
  for (const [variable, value] of Object.entries(variables)) {
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    setVariable(variable as GlobalCSSVariable, value);
  }
};

const getVariable = (variable: GlobalCSSVariable) => {
  return document.documentElement.style.getPropertyValue(variable);
};

const removeVariable = (variable: GlobalCSSVariable) => {
  document.documentElement.style.removeProperty(variable);
};

export function ThemeContextProvider({ children }: ThemeContextProviderProps) {
  const auth = useStore(authStore);
  const hasExplicitColorSchemePreference = getHasExplicitColorSchemePreference();
  const [colorScheme, setColorScheme] = useLocalStorage<ColorSchemePreference>(COLOR_SCHEME_KEY, DEFAULT_COLOR_SCHEME);

  // Apply product defaults only until the user chooses a color scheme.
  useEffect(() => {
    if (auth.account == null) return;

    if (hasExplicitColorSchemePreference === true) return;

    if (isBuilderPlan(auth.account)) {
      if (colorScheme !== 'dark') {
        setColorScheme('dark');
      }
      return;
    }

    if (colorScheme === 'light') {
      setColorScheme(DEFAULT_COLOR_SCHEME);
    }
  }, [auth.account, colorScheme, hasExplicitColorSchemePreference, setColorScheme]);

  // Reconcile the locally-cached scheme with the persisted, cross-device backend value
  // once the current user loads. localStorage drives first paint; the backend wins as
  // the source of truth, and a deliberate local choice is backfilled up when the backend
  // has none yet. Runs once per user (not on every render) so we don't fight local edits.
  const reconciledForUserRef = useRef<number | null>(null);
  useEffect(() => {
    const user = auth.user;
    if (user == null || auth.account?.is_own_account !== true || reconciledForUserRef.current === user.id) {
      return;
    }
    reconciledForUserRef.current = user.id;

    const backend = user.settings?.theme_preferences?.color_scheme ?? null;
    const result = reconcileTheme({
      backend,
      local: colorScheme,
      localExplicit: getHasExplicitColorSchemePreference(),
      isAuthenticated: true,
    });

    applyThemeReconcile(result, {
      setColorScheme,
      accountId: user.account_id,
      userId: user.id,
      colorScheme,
    });
  }, [auth.user, auth.account?.is_own_account, colorScheme, setColorScheme]);

  const setColorSchemeExplicitly = (value: ColorSchemePreference) => {
    localStorage.setItem(COLOR_SCHEME_EXPLICIT_KEY, 'true');
    setColorScheme(value);

    const user = auth.user;
    // Local toggle still applies for the user's own view, but only persist when the account
    // context is the user's own.
    if (user != null && auth.account?.is_own_account === true) {
      persistThemePreference(user.account_id, user.id, value);
    }
  };

  return (
    <ThemeContext.Provider
      value={{
        colorScheme,
        setColorScheme: setColorSchemeExplicitly,
        setVariable,
        setVariables,
        getVariable,
        removeVariable,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}
